import { useStore } from "@/store/store";
import { computed } from "vue";

/**
 * Composable for managing team leader contacts through the Vuex store.
 * Provides reactive state and methods for CRUD operations on contacts and their statuses.
 * @returns {Object} An object containing local reactive state, computed properties from the store, and methods to interact with team leader contacts.
 */
export function useTeamLeaderContacts() {
  const store = useStore();

  // Computed properties to get data from the Vuex store
  const contacts = computed(() => store.state.dashboardMyContacts.teamLeaderContacts);
  const contact = computed(() => store.state.dashboardMyContacts.currentTeamLeaderContact);
  const campaigns = computed({
    get() {
      return store.state.dashboardMyContacts.campaigns;
    },
    set(value) {
      store.commit("dashboardMyContacts/setCampaigns", value);
    },
  });
  const teams = computed({
    get() {
      return store.state.dashboardMyContacts.teams;
    },
    set(value) {
      store.commit("dashboardMyContacts/setTeams", value);
    },
  });
  const campaignId = computed({
    get() {
      return store.state.dashboardMyContacts.campaignId;
    },
    set(value) {
      store.commit("dashboardMyContacts/setCampaignId", value);
    },
  });
  const teamId = computed({
    get() {
      return store.state.dashboardMyContacts.teamId;
    },
    set(value) {
      store.commit("dashboardMyContacts/setTeamId", value);
    },
  });
  const storeLoading = computed({
    get() {
      return store.state.dashboardMyContacts.teamLeaderContactsLoading;
    },
    set(value) {
      store.commit("dashboardMyContacts/setTeamLeaderContactsLoading", value);
    },
  });
  const storeError = computed({
    get() {
      return store.state.dashboardMyContacts.teamLeaderContactsError;
    },
    set(value) {
      store.commit("dashboardMyContacts/setTeamLeaderContactsError", value);
    },
  });
  const storePagination = computed({
    get() {
      return store.state.dashboardMyContacts.teamLeaderContactsPagination;
    },
    set(value) {
      store.commit("dashboardMyContacts/setTeamLeaderContactsPagination", value);
    },
  });
  const storeLastFetchParams = computed({
    get() {
      return store.state.dashboardMyContacts.teamLeaderContactsLastFetchParams;
    },
    set(value) {
      store.commit("dashboardMyContacts/setTeamLeaderContactsLastFetchParams", value);
    },
  });
  const storeCampaignCurrency = computed({
    get() {
      return store.state.dashboardMyContacts.campaignCurrency;
    },
    set(value) {
      store.commit("dashboardMyContacts/setCampaignCurrency", value);
    },
  });
  const storeSearchQuery = computed(() => store.state.dashboardMyContacts.searchQuery);
  const storeSearchFilters = computed(() => store.state.dashboardMyContacts.searchFilters);

  function getSearchFiltersURLParams(searchFilters) {
    return {
      status: Object.entries(searchFilters)
        .filter(([, value]) => value)
        .map(([key]) => key),
    };
  }

  function resetStorePagination() {
    store.commit("dashboardMyContacts/resetStorePagination");
  }

  /**
   * Fetches a list of team leader contacts from the store.
   * Updates local pagination state based on the response.
   * @param {Object} [params={}] - Additional parameters for the API request (e.g., page, per_page, status_key).
   */
  const fetchContacts = async (params = {}) => {
    // TODO: remove this
    console.log("RUN fetchContacts", params);

    storeLoading.value = true;
    storeError.value = null;

    if (params && Object.keys(params).length > 0) {
      storeLastFetchParams.value = params;
    }

    try {
      const response = await store.dispatch("dashboardMyContacts/fetchTeamLeaderContacts", {
        page: storePagination.value.page,
        per_page: storePagination.value.per_page,
        ...storeLastFetchParams.value,
      });
      if (response && response.meta) {
        storePagination.value = {
          page: response.meta.page,
          per_page: response.meta.per_page,
          total: response.meta.total,
        };
      }
    } catch (err) {
      const errors = err.errors.map((error) => error.message).join("; ");
      storeError.value =
        err.errors.length > 0
          ? `${err.message}${errors ? `: ${errors}` : ""}`
          : "Failed to fetch contacts";
    } finally {
      storeLoading.value = false;
    }
  };

  /**
   * Creates a new team leader contact.
   * Re-fetches the contact list upon successful creation.
   * @param {Object} contactData - The data for the new contact.
   * @returns {Promise<Object>} The newly created contact data.
   * @throws {Error} If the creation fails.
   */
  const createContact = async (contactData) => {
    storeLoading.value = true;
    storeError.value = null;
    try {
      const newContact = await store.dispatch(
        "dashboardMyContacts/createTeamLeaderContact",
        contactData
      );
      return newContact;
    } catch (err) {
      const errors = err.errors.map((error) => error.message).join("; ");
      storeError.value =
        err.errors.length > 0
          ? `${err.message}${errors ? `: ${errors}` : ""}`
          : "Failed to create contact";
      throw err;
    } finally {
      storeLoading.value = false;
    }
  };

  /**
   * Fetches a single team leader contact by its ID.
   * The fetched contact will be available in `store.state.dashboardMyContacts.currentTeamLeaderContact`.
   * @param {String|Number} contactId - The ID of the contact to fetch.
   * @returns {Promise<Object>} The fetched contact data.
   * @throws {Error} If fetching fails.
   */
  const fetchContactById = async (contactId) => {
    return await store.dispatch("dashboardMyContacts/fetchTeamLeaderContactById", contactId);
  };

  /**
   * Updates an existing team leader contact.
   * Re-fetches the contact list upon successful update.
   * @param {String|Number} contactId - The ID of the contact to update.
   * @param {Object} contactData - The new data for the contact.
   * @returns {Promise<Object>} The updated contact data.
   * @throws {Error} If the update fails.
   */
  const updateContact = async (contactId, contactData) => {
    storeLoading.value = true;
    storeError.value = null;
    try {
      const updatedContact = await store.dispatch("dashboardMyContacts/updateTeamLeaderContact", {
        contactId,
        contactData,
      });
      return updatedContact;
    } catch (err) {
      const errors = err.errors.map((error) => error.message).join("; ");
      storeError.value =
        err.errors.length > 0
          ? `${err.message}${errors ? `: ${errors}` : ""}`
          : "Failed to update contact";
      throw err;
    } finally {
      storeLoading.value = false;
    }
  };

  /**
   * Updates the status of a team leader contact.
   * Re-fetches the contact list to update counts and statuses.
   * @param {String|Number} contactId - The ID of the contact whose status is to be updated.
   * @param {Object} statusData - The data for the status update (e.g., { status_key: "new_status" }).
   * @returns {Promise<Object>} The contact data after status update.
   * @throws {Error} If the status update fails.
   */
  const updateContactStatus = async (contactId, statusData) => {
    storeLoading.value = true;
    storeError.value = null;
    try {
      const updatedContact = await store.dispatch(
        "dashboardMyContacts/updateTeamLeaderContactStatus",
        {
          contactId,
          statusData,
        }
      );
      return updatedContact;
    } catch (err) {
      const errors = err.errors.map((error) => error.message).join("; ");
      storeError.value =
        err.errors.length > 0
          ? `${err.message}${errors ? `: ${errors}` : ""}`
          : "Failed to update contact status";
      throw err;
    } finally {
      storeLoading.value = false;
    }
  };

  const importContactsXlsx = async (file) => {
    if (!campaignId.value || !teamId.value) {
      throw new Error("Campaign and Team must be selected to import contacts");
    }

    await store.dispatch("dashboardMyContacts/importTeamLeaderContactsXlsx", {
      campaignId: campaignId.value,
      teamId: teamId.value,
      file,
    });
  };

  return {
    storeCampaignCurrency,
    campaigns,
    teams,
    campaignId,
    teamId,
    contacts,
    contact,
    storePagination,
    storeLoading,
    storeError,
    storeSearchQuery,
    storeSearchFilters,
    fetchContacts,
    createContact,
    fetchContactById,
    updateContact,
    updateContactStatus,
    importContactsXlsx,
    getSearchFiltersURLParams,
    resetStorePagination,
  };
}
